import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve the API base from the configured backend URL, but prefer the SAME host
// the page is served from when it's the same site (e.g. www vs apex of the same
// domain). This keeps requests first-party/same-origin so auth cookies and CORS
// work regardless of whether a user lands on apex or www. In preview the served
// host already equals REACT_APP_BACKEND_URL, so behaviour is unchanged.
function resolveApiBase() {
  const baked = BACKEND_URL || "";
  if (typeof window === "undefined" || !baked) return `${baked}/api`;
  try {
    const bakedHost = new URL(baked).hostname;
    const curHost = window.location.hostname;
    const registrable = (h) => h.split(".").slice(-2).join(".");
    if (curHost === bakedHost || registrable(curHost) === registrable(bakedHost)) {
      return `${window.location.origin}/api`;
    }
  } catch (e) {
    /* fall through to baked URL */
  }
  return `${baked}/api`;
}

export const API = resolveApiBase();

const api = axios.create({ baseURL: API, withCredentials: true, timeout: 25000 });

// Seamlessly refresh an expired access token (30m) using the refresh token (7d),
// so a signed-in agent on a second device isn't logged out mid-session.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    const url = cfg.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    if (status === 401 && !cfg._retry && !isAuthCall) {
      cfg._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(cfg);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const money = (rupees) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(rupees || 0);

export default api;
